import { STAGE_CONFIG } from '@/config';

export function welcomeAllowed(isImpersonating: boolean): boolean {
  if (!isImpersonating) return true;
  return STAGE_CONFIG.stage !== 'prod';
}

// Only the *key* welcome needs this. That dialog's whole point is the copy
// affordance, so it can only run where the plaintext key exists — the create
// response, or a freshly provisioned row on a page that fetched the account
// record. The account welcome is driven app-wide from App.vue instead, on the
// one-shot claim alone; the /v1/auth/me summaries carry no timestamp.
const RECENTLY_CREATED_WINDOW_MS = 10 * 60 * 1000;

export function isRecentlyCreated(createdAt: string | undefined): boolean {
  // Absent on pre-backfill records, which are by definition old.
  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;

  return Date.now() - created < RECENTLY_CREATED_WINDOW_MS;
}

// One-shot, backed by localStorage so neither a reload nor a new tab can
// re-open a dialog the user already dismissed.
const ACCOUNT_CLAIM_PREFIX = 'parley.welcome.account';
const KEY_CLAIM_PREFIX = 'parley.welcome.key';
// Feature announcements are versioned in the key so a *later* announcement can
// be shown to people who already dismissed this one. Never re-use a version to
// re-show the same announcement: existing users have the flag set for life.
const RAG_ANNOUNCEMENT_CLAIM_PREFIX = 'parley.announce.rag.v1';

const claimedInSession = new Set<string>();

function storageKeyFor(prefix: string, kerberos: string): string {
  return `${prefix}.${kerberos || 'unknown'}`;
}

// Read-only: does NOT take the claim. Lets a caller skip the work that would
// precede a claim (a flag fetch, say) without burning it.
function alreadyClaimed(prefix: string, kerberos: string): boolean {
  const storageKey = storageKeyFor(prefix, kerberos);
  if (claimedInSession.has(storageKey)) return true;
  try {
    return localStorage.getItem(storageKey) !== null;
  } catch {
    // localStorage unavailable — the in-session flag is all we have, and it
    // says no. claimOnce() below makes the same assumption.
    return false;
  }
}

function claimOnce(prefix: string, kerberos: string): boolean {
  const storageKey = storageKeyFor(prefix, kerberos);
  if (claimedInSession.has(storageKey)) return false;
  try {
    if (localStorage.getItem(storageKey) !== null) {
      claimedInSession.add(storageKey);
      return false;
    }
    localStorage.setItem(storageKey, '1');
  } catch {
    // localStorage unavailable — fall through on the in-memory flag alone.
  }
  claimedInSession.add(storageKey);
  return true;
}

export function claimAccountWelcome(kerberos: string): boolean {
  return claimOnce(ACCOUNT_CLAIM_PREFIX, kerberos);
}

export function claimKeyWelcome(kerberos: string): boolean {
  return claimOnce(KEY_CLAIM_PREFIX, kerberos);
}

// The "knowledge bases are here" announcement. Gated on the platform-wide
// Knowledge Base switch by the caller, so an account that never sees the
// feature never burns the claim on it.
export function claimRagAnnouncement(kerberos: string): boolean {
  return claimOnce(RAG_ANNOUNCEMENT_CLAIM_PREFIX, kerberos);
}

// Lets App.vue skip the Knowledge Base flag request on the overwhelmingly
// common path where the user has already seen the announcement.
export function ragAnnouncementSeen(kerberos: string): boolean {
  return alreadyClaimed(RAG_ANNOUNCEMENT_CLAIM_PREFIX, kerberos);
}
